import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MultiSelectComboBox } from '@/components/automation/content/shared/MultiSelectComboBox';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertCircle, RotateCcw, Eye, Save, X, Target, BarChart3, Users, TrendingUp, CheckCircle, Sparkles, Settings, Zap, Clock, HelpCircle, Bell, Mail, Check, ChevronsUpDown, FileText, Bot, User, Plus, Palette, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ColorPicker } from '@/components/ui/color-picker';

interface ScorecardParameter {
  id: string;
  name: string;
  description: string;
  scale: string;
  enabled: boolean;
  weight: number;
}

interface ScorecardWeights {
  agent: ScorecardParameter[];
  bot: ScorecardParameter[];
}

interface QADelaySettings {
  value: number;
  unit: 'minutes' | 'hours' | 'days';
}

interface ScoreLabelBand {
  id: string;
  minScore: number;
  maxScore: number;
  label: string;
  color: string;
}

interface ScoreLabelBands {
  allowHalfPoints: boolean;
  bands: ScoreLabelBand[];
}

interface QASettings {
  delay: QADelaySettings;
  confidenceThreshold: number;
  aiConfidenceThreshold?: number;
  frequency: {
    type: 'all' | 'percentage' | 'interval';
    value: number;
  };
  ratingScale: '1-5' | '1-10' | '0-100';
  acceptableThreshold: number;
  scoreLabelBands: ScoreLabelBands;
  reviewDisputeWindow: {
    value: number;
    unit: 'hours' | 'days';
  };
  supervisorDisputeTAT: {
    value: number;
    unit: 'hours' | 'days';
  };
  supervisorNotifications: {
    scoreBelow: boolean;
    negativeParameters: boolean;
    selectedParameters: string[];
    method: 'in-app' | 'email';
  };
}

interface ScoringAgentPanelProps {
  teamId?: string;
  onSave: (scorecardData: {
    name: string;
    description: string;
    teamId: string;
    status: 'draft' | 'live';
    weights: ScorecardWeights;
    qaSettings: QASettings;
  }) => void;
  onCancel: () => void;
  teams?: Array<{
    id: string;
    name: string;
  }>;
  initialData?: {
    name?: string;
    description?: string;
    teamId?: string;
    status?: 'draft' | 'live';
    weights?: ScorecardWeights;
    qaSettings?: QASettings;
  };
}

const DEFAULT_AGENT_PARAMETERS: ScorecardParameter[] = [{
  id: 'greeting',
  name: 'Greeting',
  description: 'Quality of opening interaction',
  scale: 'Y/N',
  enabled: true,
  weight: 12.5
}, {
  id: 'empathy',
  name: 'Empathy',
  description: 'Shows understanding and care',
  scale: 'Y/N',
  enabled: true,
  weight: 12.5
}, {
  id: 'clarity',
  name: 'Clarity',
  description: 'Clear and easy to understand',
  scale: '1–3',
  enabled: true,
  weight: 12.5
}, {
  id: 'solution',
  name: 'Solution Offered',
  description: 'Provides helpful solutions',
  scale: 'Y/N/N/A',
  enabled: true,
  weight: 12.5
}, {
  id: 'closing',
  name: 'Closing',
  description: 'Professional conversation end',
  scale: 'Y/N',
  enabled: true,
  weight: 12.5
}, {
  id: 'tone',
  name: 'Tone',
  description: 'Appropriate and professional tone',
  scale: 'Positive/Neutral/Negative',
  enabled: true,
  weight: 12.5
}, {
  id: 'grammar',
  name: 'Spelling & Grammar',
  description: 'Correct language usage',
  scale: 'Error count or 1–3',
  enabled: true,
  weight: 12.5
}, {
  id: 'comprehension',
  name: 'Comprehension/Understanding',
  description: 'Understanding customer needs',
  scale: 'Y/N',
  enabled: true,
  weight: 12.5
}];

const DEFAULT_BOT_PARAMETERS: ScorecardParameter[] = [{
  id: 'greeting',
  name: 'Greeting/Opening',
  description: 'Effective bot introduction',
  scale: 'Y/N',
  enabled: true,
  weight: 20
}, {
  id: 'clarity',
  name: 'Clarity',
  description: 'Clear bot responses',
  scale: '1–3',
  enabled: true,
  weight: 20
}, {
  id: 'handoff',
  name: 'Hand-off Effectiveness',
  description: 'Smooth transition to human',
  scale: 'Y/N',
  enabled: true,
  weight: 20
}, {
  id: 'escalation',
  name: 'Escalation as Needed',
  description: 'Appropriate escalation timing',
  scale: 'Y/N',
  enabled: true,
  weight: 20
}, {
  id: 'tone',
  name: 'Tone',
  description: 'Consistent brand voice',
  scale: 'Positive/Neutral/Negative',
  enabled: true,
  weight: 20
}];

// Default score label bands for different scales
const getDefaultScoreLabelBands = (scale: '1-5' | '1-10' | '0-100'): ScoreLabelBands => {
  switch (scale) {
    case '1-5':
      return {
        allowHalfPoints: true,
        bands: [{
          id: '1',
          minScore: 1.0,
          maxScore: 2.4,
          label: 'Poor',
          color: '#ef4444'
        }, {
          id: '2',
          minScore: 2.5,
          maxScore: 3.4,
          label: 'Below Average',
          color: '#f97316'
        }, {
          id: '3',
          minScore: 3.5,
          maxScore: 4.4,
          label: 'Acceptable',
          color: '#eab308'
        }, {
          id: '4',
          minScore: 4.5,
          maxScore: 5.0,
          label: 'Exceeded Expectations',
          color: '#22c55e'
        }]
      };
    case '1-10':
      return {
        allowHalfPoints: true,
        bands: [{
          id: '1',
          minScore: 1.0,
          maxScore: 4.4,
          label: 'Poor',
          color: '#ef4444'
        }, {
          id: '2',
          minScore: 4.5,
          maxScore: 6.4,
          label: 'Below Average',
          color: '#f97316'
        }, {
          id: '3',
          minScore: 6.5,
          maxScore: 8.4,
          label: 'Acceptable',
          color: '#eab308'
        }, {
          id: '4',
          minScore: 8.5,
          maxScore: 10.0,
          label: 'Exceeded Expectations',
          color: '#22c55e'
        }]
      };
    case '0-100':
      return {
        allowHalfPoints: false,
        bands: [{
          id: '1',
          minScore: 0,
          maxScore: 49,
          label: 'Poor',
          color: '#ef4444'
        }, {
          id: '2',
          minScore: 50,
          maxScore: 69,
          label: 'Below Average',
          color: '#f97316'
        }, {
          id: '3',
          minScore: 70,
          maxScore: 84,
          label: 'Acceptable',
          color: '#eab308'
        }, {
          id: '4',
          minScore: 85,
          maxScore: 94,
          label: 'Above Expectations',
          color: '#3b82f6'
        }, {
          id: '5',
          minScore: 95,
          maxScore: 100,
          label: 'Exceeded Expectations',
          color: '#22c55e'
        }]
      };
    default:
      return getDefaultScoreLabelBands('0-100');
  }
};

export const ScoringAgentPanel: React.FC<ScoringAgentPanelProps> = ({
  teamId,
  onSave,
  onCancel,
  teams = [],
  initialData
}) => {
  const [agentName, setAgentName] = useState(initialData?.name || '');
  const [agentDescription, setAgentDescription] = useState(initialData?.description || '');
  const [selectedTeamId, setSelectedTeamId] = useState(initialData?.teamId || teamId || '');
  const [agentStatus, setAgentStatus] = useState<'draft' | 'live'>(initialData?.status || 'draft');
  const [weights, setWeights] = useState<ScorecardWeights>(initialData?.weights || {
    agent: DEFAULT_AGENT_PARAMETERS,
    bot: DEFAULT_BOT_PARAMETERS
  });
  const [qaSettings, setQaSettings] = useState<QASettings>(initialData?.qaSettings || {
    delay: {
      value: 30,
      unit: 'minutes'
    },
    confidenceThreshold: 85,
    frequency: {
      type: 'all',
      value: 100
    },
    ratingScale: '0-100',
    acceptableThreshold: 70,
    scoreLabelBands: getDefaultScoreLabelBands('0-100'),
    reviewDisputeWindow: {
      value: 7,
      unit: 'days'
    },
    supervisorDisputeTAT: {
      value: 3,
      unit: 'days'
    },
    supervisorNotifications: {
      scoreBelow: true,
      negativeParameters: false,
      selectedParameters: [],
      method: 'in-app'
    }
  });
  const [activeTab, setActiveTab] = useState<'agent' | 'bot'>('agent');
  const [showPreview, setShowPreview] = useState(false);
  
  // Collapsible section states
  const [basicConfigOpen, setBasicConfigOpen] = useState(true);
  const [scoreBandsOpen, setScoreBandsOpen] = useState(true);
  const [evaluationParamsOpen, setEvaluationParamsOpen] = useState(true);

  const calculateTotal = (parameters: ScorecardParameter[]): number => {
    return parameters.filter(p => p.enabled).reduce((sum, p) => sum + p.weight, 0);
  };

  const redistributeWeights = (parameters: ScorecardParameter[]): ScorecardParameter[] => {
    const enabledParams = parameters.filter(p => p.enabled);
    if (enabledParams.length === 0) return parameters;
    const equalWeight = Math.round(100 / enabledParams.length * 10) / 10;
    let remaining = 100;
    return parameters.map((param, index) => {
      if (!param.enabled) return {
        ...param,
        weight: 0
      };
      const enabledIndex = enabledParams.findIndex(p => p.id === param.id);
      if (enabledIndex === enabledParams.length - 1) {
        // Last enabled parameter gets the remaining weight
        return {
          ...param,
          weight: Math.round(remaining * 10) / 10
        };
      } else {
        remaining -= equalWeight;
        return {
          ...param,
          weight: equalWeight
        };
      }
    });
  };

  const handleWeightChange = (type: 'agent' | 'bot', paramId: string, newWeight: number) => {
    setWeights(prev => ({
      ...prev,
      [type]: prev[type].map(param => param.id === paramId ? {
        ...param,
        weight: newWeight
      } : param)
    }));
  };

  const handleToggleParameter = (type: 'agent' | 'bot', paramId: string) => {
    setWeights(prev => {
      const updatedParams = prev[type].map(param => param.id === paramId ? {
        ...param,
        enabled: !param.enabled
      } : param);

      // Redistribute weights when toggling
      const redistributed = redistributeWeights(updatedParams);
      return {
        ...prev,
        [type]: redistributed
      };
    });
  };

  const handleRestoreDefaults = () => {
    setWeights({
      agent: DEFAULT_AGENT_PARAMETERS,
      bot: DEFAULT_BOT_PARAMETERS
    });
  };

  const isValidTotal = (type: 'agent' | 'bot'): boolean => {
    const total = calculateTotal(weights[type]);
    return Math.abs(total - 100) < 0.1; // Allow for small rounding differences
  };

  const canSave = isValidTotal('agent') && isValidTotal('bot') && agentName.trim() && selectedTeamId && agentStatus && qaSettings.delay.value > 0;

  // Get all available parameters for multi-select
  const getAvailableParameters = () => {
    const agentParams = weights.agent.filter(param => param.enabled).map(param => ({
      id: `agent-${param.id}`,
      name: `Agent: ${param.name}`,
      category: 'agent'
    }));
    const botParams = weights.bot.filter(param => param.enabled).map(param => ({
      id: `bot-${param.id}`,
      name: `Bot: ${param.name}`,
      category: 'bot'
    }));
    return [...agentParams, ...botParams];
  };

  // Score Label Bands functions
  const addScoreBand = () => {
    const newBand: ScoreLabelBand = {
      id: Date.now().toString(),
      minScore: 0,
      maxScore: 10,
      label: 'New Band',
      color: '#6b7280'
    };
    setQaSettings(prev => ({
      ...prev,
      scoreLabelBands: {
        ...prev.scoreLabelBands,
        bands: [...prev.scoreLabelBands.bands, newBand]
      }
    }));
  };

  const removeBand = (bandId: string) => {
    setQaSettings(prev => ({
      ...prev,
      scoreLabelBands: {
        ...prev.scoreLabelBands,
        bands: prev.scoreLabelBands.bands.filter(band => band.id !== bandId)
      }
    }));
  };

  const updateBand = (bandId: string, field: keyof ScoreLabelBand, value: any) => {
    setQaSettings(prev => ({
      ...prev,
      scoreLabelBands: {
        ...prev.scoreLabelBands,
        bands: prev.scoreLabelBands.bands.map(band => band.id === bandId ? {
          ...band,
          [field]: value
        } : band)
      }
    }));
  };

  const restoreDefaultBands = () => {
    setQaSettings(prev => ({
      ...prev,
      scoreLabelBands: getDefaultScoreLabelBands(prev.ratingScale)
    }));
  };

  const validateBands = (): {
    isValid: boolean;
    message?: string;
  } => {
    const bands = qaSettings.scoreLabelBands.bands.sort((a, b) => a.minScore - b.minScore);
    for (let i = 0; i < bands.length; i++) {
      if (bands[i].minScore >= bands[i].maxScore) {
        return {
          isValid: false,
          message: `Band "${bands[i].label}" has invalid range`
        };
      }
      if (i > 0 && bands[i].minScore <= bands[i - 1].maxScore) {
        return {
          isValid: false,
          message: `Bands "${bands[i - 1].label}" and "${bands[i].label}" overlap`
        };
      }
    }
    return {
      isValid: true
    };
  };

  const getPreviewForScore = (score: number) => {
    // Sort bands by minScore to ensure proper matching
    const sortedBands = qaSettings.scoreLabelBands.bands.sort((a, b) => a.minScore - b.minScore);
    const band = sortedBands.find(b => score >= b.minScore && score <= b.maxScore);
    return band ? {
      label: band.label || 'Untitled',
      color: band.color
    } : null;
  };

  const renderParameterRow = (param: ScorecardParameter, type: 'agent' | 'bot') => {
    const total = calculateTotal(weights[type]);
    const isOverLimit = total > 100;
    return (
      <div key={param.id} className="flex items-center gap-4 p-4 rounded-lg border bg-card">
        <Switch checked={param.enabled} onCheckedChange={() => handleToggleParameter(type, param.id)} />
        
        <div className="flex-1 min-w-0 space-y-1">
          <Label className="font-medium">{param.name}</Label>
          <p className="text-sm text-muted-foreground">{param.description}</p>
          <Badge variant="outline" className="text-xs">
            Scale: {param.scale}
          </Badge>
        </div>
        
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-32">
            <Slider disabled={!param.enabled} value={[param.weight]} onValueChange={([value]) => handleWeightChange(type, param.id, value)} max={100} step={0.5} className="w-full" />
          </div>
          
          <div className="w-16">
            <Input disabled={!param.enabled} type="number" value={param.weight} onChange={e => handleWeightChange(type, param.id, parseFloat(e.target.value) || 0)} min={0} max={100} step={0.5} className="text-center" />
          </div>
          
          <span className="text-sm font-medium min-w-[20px]">%</span>
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="w-full max-w-7xl mx-auto p-6 space-y-8">
        {/* Basic Configuration Section */}
        <Collapsible open={basicConfigOpen} onOpenChange={setBasicConfigOpen}>
          <Card className="border-border/60 shadow-sm">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="pb-4 hover:bg-muted/30 transition-colors">
                <CardTitle className="flex items-center justify-between text-lg font-semibold">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <Settings className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    Basic Configuration
                  </div>
                  {basicConfigOpen ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-left">
                  Configure basic scorecard settings and metadata
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 pb-6">
                {/* Scorecard Identity */}
                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-semibold">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      Scorecard Identity
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      Configure basic scorecard settings and metadata
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="scorecard-name" className="text-sm font-medium">
                        Scorecard Name
                      </Label>
                      <Input id="scorecard-name" value={agentName} onChange={e => setAgentName(e.target.value)} placeholder="Enter scorecard name" className="w-full" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="scorecard-description" className="text-sm font-medium">
                        Description
                      </Label>
                      <Textarea id="scorecard-description" value={agentDescription} onChange={e => setAgentDescription(e.target.value)} placeholder="Describe the purpose and scope of this scorecard" className="w-full min-h-[80px] resize-none" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Status</Label>
                      <Select value={agentStatus} onValueChange={value => setAgentStatus(value as 'draft' | 'live')}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="live">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              Live
                            </div>
                          </SelectItem>
                          <SelectItem value="draft">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                              Draft
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* QA Configuration */}
                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-semibold">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <Settings className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      QA Configuration
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      Configure quality assurance settings and thresholds
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">QA Delay</Label>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={qaSettings.delay.value} onChange={e => setQaSettings(prev => ({
                        ...prev,
                        delay: {
                          ...prev.delay,
                          value: parseInt(e.target.value) || 0
                        }
                      }))} className="w-20 text-center" min={1} />
                        <Select value={qaSettings.delay.unit} onValueChange={value => setQaSettings(prev => ({
                        ...prev,
                        delay: {
                          ...prev.delay,
                          unit: value as 'minutes' | 'hours' | 'days'
                        }
                      }))}>
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="minutes">Minutes</SelectItem>
                            <SelectItem value="hours">Hours</SelectItem>
                            <SelectItem value="days">Days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Target Team</Label>
                      <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                        <SelectTrigger className="w-full bg-background border-border/60 hover:border-border/80 transition-colors">
                          <SelectValue placeholder="Select team for this scorecard" />
                        </SelectTrigger>
                        <SelectContent className="bg-background border-border/80 shadow-lg z-50">
                          {teams.map(team => (
                            <SelectItem key={team.id} value={team.id} className="hover:bg-muted/80 focus:bg-muted/80">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                {team.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Confidence Threshold ({qaSettings.confidenceThreshold}%)</Label>
                      <Slider value={[qaSettings.confidenceThreshold]} onValueChange={value => setQaSettings(prev => ({
                      ...prev,
                      confidenceThreshold: value[0]
                    }))} max={100} min={0} step={5} className="w-full" />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">AI Confidence Threshold ({qaSettings.aiConfidenceThreshold || 85}%)</Label>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">Minimum confidence percentage required before AI publishes a rating</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Slider 
                        value={[qaSettings.aiConfidenceThreshold || 85]} 
                        onValueChange={value => setQaSettings(prev => ({
                          ...prev,
                          aiConfidenceThreshold: value[0]
                        }))} 
                        max={100} 
                        min={50} 
                        step={5} 
                        className="w-full" 
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>50% - Lower threshold</span>
                        <span>100% - Higher threshold</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">Review/Dispute Window</Label>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">Time limit for agents to submit reviews or disputes for their evaluations</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input 
                          type="number" 
                          value={qaSettings.reviewDisputeWindow.value} 
                          onChange={e => setQaSettings(prev => ({
                            ...prev,
                            reviewDisputeWindow: {
                              ...prev.reviewDisputeWindow,
                              value: parseInt(e.target.value) || 1
                            }
                          }))} 
                          className="w-20 text-center" 
                          min={1} 
                        />
                        <Select 
                          value={qaSettings.reviewDisputeWindow.unit} 
                          onValueChange={value => setQaSettings(prev => ({
                            ...prev,
                            reviewDisputeWindow: {
                              ...prev.reviewDisputeWindow,
                              unit: value as 'hours' | 'days'
                            }
                          }))}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hours">Hours</SelectItem>
                            <SelectItem value="days">Days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">Supervisor Dispute TAT</Label>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">Turn around time enforced on supervisors to respond to review disputes</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input 
                          type="number" 
                          value={qaSettings.supervisorDisputeTAT.value} 
                          onChange={e => setQaSettings(prev => ({
                            ...prev,
                            supervisorDisputeTAT: {
                              ...prev.supervisorDisputeTAT,
                              value: parseInt(e.target.value) || 1
                            }
                          }))} 
                          className="w-20 text-center" 
                          min={1} 
                        />
                        <Select 
                          value={qaSettings.supervisorDisputeTAT.unit} 
                          onValueChange={value => setQaSettings(prev => ({
                            ...prev,
                            supervisorDisputeTAT: {
                              ...prev.supervisorDisputeTAT,
                              unit: value as 'hours' | 'days'
                            }
                          }))}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hours">Hours</SelectItem>
                            <SelectItem value="days">Days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Frequency Settings */}
                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-semibold">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <Clock className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      Frequency Settings
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      Configure when and how often conversations are evaluated
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">QA Frequency</Label>
                      <Select value={qaSettings.frequency.type} onValueChange={value => setQaSettings(prev => ({
                      ...prev,
                      frequency: {
                        ...prev.frequency,
                        type: value as 'all' | 'percentage' | 'interval'
                      }
                    }))}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Conversations</SelectItem>
                          <SelectItem value="percentage">Percentage Sample</SelectItem>
                          <SelectItem value="interval">Every N Conversations</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {qaSettings.frequency.type !== 'all' && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          {qaSettings.frequency.type === 'percentage' ? 'Percentage (%)' : 'Interval (N)'}
                        </Label>
                        <Input type="number" value={qaSettings.frequency.value} onChange={e => setQaSettings(prev => ({
                      ...prev,
                      frequency: {
                        ...prev.frequency,
                        value: parseInt(e.target.value) || 1
                      }
                    }))} min={1} max={qaSettings.frequency.type === 'percentage' ? 100 : undefined} className="w-full" />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Rating Scale</Label>
                      <Select value={qaSettings.ratingScale} onValueChange={value => {
                      const newScale = value as '1-5' | '1-10' | '0-100';
                      setQaSettings(prev => ({
                        ...prev,
                        ratingScale: newScale,
                        scoreLabelBands: getDefaultScoreLabelBands(newScale)
                      }));
                    }}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select scale" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-5">1-5 Scale</SelectItem>
                          <SelectItem value="1-10">1-10 Scale</SelectItem>
                          <SelectItem value="0-100">0-100 Scale</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Notification Settings */}
                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-semibold">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <Bell className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      Notification Settings
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      Configure alerts for supervisors and managers
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Notification Method</Label>
                      <Select value={qaSettings.supervisorNotifications.method} onValueChange={value => setQaSettings(prev => ({
                      ...prev,
                      supervisorNotifications: {
                        ...prev.supervisorNotifications,
                        method: value as 'in-app' | 'email'
                      }
                    }))}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in-app">
                            <div className="flex items-center gap-2">
                              <Bell className="h-4 w-4" />
                              In-App Notifications
                            </div>
                          </SelectItem>
                          <SelectItem value="email">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              Email Notifications
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Score Below Notifications</Label>
                        <Switch checked={qaSettings.supervisorNotifications.scoreBelow} onCheckedChange={checked => setQaSettings(prev => ({
                        ...prev,
                        supervisorNotifications: {
                          ...prev.supervisorNotifications,
                          scoreBelow: checked
                        }
                      }))} />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Negative Parameter Alerts</Label>
                        <Switch checked={qaSettings.supervisorNotifications.negativeParameters} onCheckedChange={checked => setQaSettings(prev => ({
                        ...prev,
                        supervisorNotifications: {
                          ...prev.supervisorNotifications,
                          negativeParameters: checked
                        }
                      }))} />
                      </div>
                    </div>

                    {qaSettings.supervisorNotifications.negativeParameters && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Specific Parameters to Monitor</Label>
                        <MultiSelectComboBox items={getAvailableParameters() || []} selectedItems={qaSettings.supervisorNotifications.selectedParameters || []} onChange={value => setQaSettings(prev => ({
                      ...prev,
                      supervisorNotifications: {
                        ...prev.supervisorNotifications,
                        selectedParameters: value
                      }
                    }))} placeholder="Select parameters to monitor" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Score Label Bands Section */}
        <Collapsible open={scoreBandsOpen} onOpenChange={setScoreBandsOpen}>
          <Card className="border-border/50 shadow-lg bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-xl transition-all duration-300">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="pb-4 border-b border-border/30 hover:bg-muted/30 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <CardTitle className="flex items-center gap-3 text-xl font-bold">
                      <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md">
                        <Palette className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <div className="text-lg">Score Label Bands</div>
                          {scoreBandsOpen ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="text-sm font-normal text-muted-foreground">
                          Customize labels and colors for {qaSettings.ratingScale} score ranges
                        </div>
                      </div>
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        restoreDefaultBands();
                      }} 
                      className="h-9 px-4 text-sm font-medium hover:bg-muted/80 transition-colors"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        addScoreBand();
                      }} 
                      className="h-9 px-4 text-sm font-medium bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Band
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="space-y-6 p-6">
                {/* Enhanced Half-point toggle */}
                {(qaSettings.ratingScale === '1-5' || qaSettings.ratingScale === '1-10') && (
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-muted/50 to-muted/30 rounded-xl border border-muted/60 hover:border-muted/80 transition-all duration-200">
                    <div className="space-y-1">
                      <Label className="text-sm font-semibold text-foreground">Enable Half-Point Scoring</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow granular scoring with 0.5 increments (e.g., 3.5, 4.5)
                      </p>
                    </div>
                    <Switch 
                      checked={qaSettings.scoreLabelBands.allowHalfPoints} 
                      onCheckedChange={checked => setQaSettings(prev => ({
                        ...prev,
                        scoreLabelBands: {
                          ...prev.scoreLabelBands,
                          allowHalfPoints: checked
                        }
                      }))} 
                      className="data-[state=checked]:bg-purple-600 scale-110" 
                    />
                  </div>
                )}

                {/* Validation alerts */}
                {(() => {
                const validation = validateBands();
                return !validation.isValid && (
                  <Alert className="border-destructive/30 bg-destructive/5 animate-fade-in py-2">
                    <AlertCircle className="h-3 w-3" />
                    <AlertDescription className="text-xs">{validation.message}</AlertDescription>
                  </Alert>
                );
              })()}

                {/* Compact Score bands */}
                <div className="space-y-2">
                  {qaSettings.scoreLabelBands.bands.sort((a, b) => a.minScore - b.minScore).map((band, index) => (
                    <div key={band.id} className="group relative overflow-hidden rounded-lg border border-border/40 bg-card hover:border-border/60 transition-all duration-200 animate-fade-in" style={{
                    animationDelay: `${index * 50}ms`
                  }}>
                      {/* Compact header and content */}
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-6 h-6 rounded bg-primary/10 border border-primary/20">
                              <span className="text-xs font-medium text-primary">{index + 1}</span>
                            </div>
                            <span className="text-sm font-medium">Band {index + 1}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge className="text-xs px-2 py-0.5 font-medium" style={{
                              backgroundColor: band.color,
                              color: 'white',
                              border: 'none'
                            }}>
                              {band.label}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => removeBand(band.id)} className="h-7 w-7 p-0 opacity-60 hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Grid layout for controls */}
                        <div className="grid grid-cols-4 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Min Score</Label>
                            <Input type="number" value={band.minScore} onChange={e => updateBand(band.id, 'minScore', parseFloat(e.target.value) || 0)} step="0.5" className="h-8 text-xs text-center" />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Max Score</Label>
                            <Input type="number" value={band.maxScore} onChange={e => updateBand(band.id, 'maxScore', parseFloat(e.target.value) || 0)} step="0.5" className="h-8 text-xs text-center" />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Label</Label>
                            <Input type="text" value={band.label} onChange={e => updateBand(band.id, 'label', e.target.value)} placeholder="Enter label" className="h-8 text-xs" />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Color</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="h-8 w-full p-1 border-2 hover:scale-105 transition-transform" style={{
                                  backgroundColor: band.color
                                }}>
                                  <div className="w-full h-full rounded-sm" style={{
                                    backgroundColor: band.color
                                  }} />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" side="right" align="start">
                                <ColorPicker color={band.color} onChange={color => updateBand(band.id, 'color', color)} />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Evaluation Parameters Section */}
        <Collapsible open={evaluationParamsOpen} onOpenChange={setEvaluationParamsOpen}>
          <Card className="border-border/50 shadow-lg">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="pb-4 hover:bg-muted/30 transition-colors">
                <CardTitle className="flex items-center justify-between text-xl font-bold">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-md">
                      <BarChart3 className="h-5 w-5 text-white" />
                    </div>
                    Evaluation Parameters
                  </div>
                  {evaluationParamsOpen ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-left">
                  Configure scoring parameters for agents and bots
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Scorecard Parameters</h3>
                  <Button variant="outline" onClick={handleRestoreDefaults} className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4" />
                    Restore Defaults
                  </Button>
                </div>

                <Tabs value={activeTab} onValueChange={value => setActiveTab(value as 'agent' | 'bot')} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="agent" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Agent Parameters
                      <Badge variant="outline" className="ml-2">
                        {weights.agent.filter(p => p.enabled).length} enabled
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="bot" className="flex items-center gap-2">
                      <Bot className="h-4 w-4" />
                      Bot Parameters
                      <Badge variant="outline" className="ml-2">
                        {weights.bot.filter(p => p.enabled).length} enabled
                      </Badge>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="agent" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <h4 className="font-medium">Agent Parameters</h4>
                        <p className="text-sm text-muted-foreground">Configure evaluation criteria for human agents</p>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${isValidTotal('agent') ? 'text-green-600' : 'text-red-600'}`}>
                          Total: {calculateTotal(weights.agent).toFixed(1)}%
                        </div>
                        {!isValidTotal('agent') && <p className="text-xs text-red-500">Must equal 100%</p>}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {weights.agent.map(param => renderParameterRow(param, 'agent'))}
                    </div>
                  </TabsContent>

                  <TabsContent value="bot" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <h4 className="font-medium">Bot Parameters</h4>
                        <p className="text-sm text-muted-foreground">Configure evaluation criteria for chatbots and automated responses</p>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${isValidTotal('bot') ? 'text-green-600' : 'text-red-600'}`}>
                          Total: {calculateTotal(weights.bot).toFixed(1)}%
                        </div>
                        {!isValidTotal('bot') && <p className="text-xs text-red-500">Must equal 100%</p>}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {weights.bot.map(param => renderParameterRow(param, 'bot'))}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

      </div>
    </TooltipProvider>
  );
};